import { useCallback, useEffect, useState } from 'react';
import { Eye, Loader2, MousePointerClick, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  formatEngagementWhen,
  loadSubscriberEngagement,
  type SubscriberEngagementRow,
} from '../../lib/emailEngagement';

function campaignLabel(r: SubscriberEngagementRow): string {
  const send = r.scheduled_emails;
  if (send?.newsletters?.name?.trim()) return send.newsletters.name.trim();
  const stepName = send?.automation_steps?.name?.trim();
  if (stepName) return stepName;
  const subject = send?.subject?.trim();
  if (subject) return subject;
  if (send?.newsletter_id) return send.newsletter_id;
  return 'Email';
}

export default function SubscriberEngagementSection({ subscriberId }: { subscriberId: string }) {
  const [rows, setRows] = useState<SubscriberEngagementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await loadSubscriberEngagement(subscriberId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load engagement');
    } finally {
      setLoading(false);
    }
  }, [subscriberId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
            Email engagement
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            When = Postmark open/click time (ReceivedAt), not when the email was sent.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          aria-label="Refresh engagement"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && rows.length === 0 ? (
        <div className="p-10 flex justify-center">
          <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="p-6 text-sm text-red-700 dark:text-red-300">{error}</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-sm text-gray-500 dark:text-gray-400 text-center">
          No opens or clicks recorded for this subscriber yet.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <th className="text-left px-4 py-2 font-medium">When</th>
              <th className="text-left px-4 py-2 font-medium">Event</th>
              <th className="text-left px-4 py-2 font-medium">Send</th>
              <th className="text-left px-4 py-2 font-medium">Link</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {rows.map((r) => (
              <tr key={r.id} className="text-gray-800 dark:text-gray-200">
                <td className="px-4 py-2 whitespace-nowrap text-gray-600 dark:text-gray-300">
                  {formatEngagementWhen(r.occurred_at)}
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      r.event_type === 'open'
                        ? 'bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200'
                        : 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200'
                    }`}
                  >
                    {r.event_type === 'open' ? (
                      <Eye className="w-3 h-3" />
                    ) : (
                      <MousePointerClick className="w-3 h-3" />
                    )}
                    {r.event_type === 'open' ? 'Open' : 'Click'}
                  </span>
                </td>
                <td className="px-4 py-2">
                  {r.scheduled_emails?.newsletter_id ? (
                    <Link
                      to={`/outgoing-newsletters/${r.scheduled_emails.newsletter_id}/content`}
                      className="text-emerald-600 dark:text-emerald-400 hover:underline truncate block max-w-[240px]"
                      title={campaignLabel(r)}
                    >
                      {campaignLabel(r)}
                    </Link>
                  ) : (
                    <span
                      className="truncate block max-w-[240px] text-gray-700 dark:text-gray-300"
                      title={campaignLabel(r)}
                    >
                      {campaignLabel(r)}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 max-w-[280px] truncate text-xs text-gray-600 dark:text-gray-300">
                  {r.event_type === 'click' && r.destination_url ? (
                    <a
                      href={r.destination_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:underline text-emerald-600 dark:text-emerald-400"
                      title={r.destination_url}
                    >
                      {r.destination_url}
                    </a>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
